import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Download, ArrowRight } from "lucide-react";
import { FadeIn } from "../components/FadeIn";
import { PageHeader } from "../components/PageHeader";
import { Section, Overline } from "../components/Primitives";
import { IMAGES } from "../data/content";
import { getPublications } from "../lib/api";
import { useContent } from "../context/ContentContext";

const TABS = [
  { key: "report", label: "Reports" },
  { key: "article", label: "Articles" },
  { key: "research", label: "Research Papers" },
  { key: "newsletter", label: "Newsletters" },
  { key: "resource", label: "Other Resources" },
];

export default function Publications() {
  const { t } = useContent();
  const [pubs, setPubs] = useState([]);
  const [tab, setTab] = useState("report");

  useEffect(() => {
    getPublications().then(setPubs).catch(() => setPubs([]));
  }, []);

  const items = pubs.filter((p) => p.category === tab);

  return (
    <div data-testid="publications-page">
      <PageHeader
        eyebrow={t("pubs.header.eyebrow")}
        title={t("pubs.header.title")}
        intro={t("pubs.header.intro")}
        image={IMAGES.classroomLibrary}
      />

      <Section className="bg-amf-cream">
        {/* Tabs */}
        <div className="flex flex-wrap gap-x-8 gap-y-3 border-b border-amf-gold/30 pb-4 mb-12">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`pub-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`amf-overline pb-1 border-b-2 -mb-[18px] transition-colors ${
                tab === t.key
                  ? "text-amf-burgundy border-amf-burgundy"
                  : "text-amf-stone border-transparent hover:text-amf-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {items.length ? (
          <div className="space-y-px bg-amf-gold/30 border border-amf-gold/30">
            {items.map((p) => (
              <FadeIn key={p.id} className="bg-amf-cream p-8 md:p-10" data-testid="publication-item">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  <div className="lg:col-span-9">
                    <span className="amf-overline text-amf-stone">{p.date}</span>
                    <h3 className="font-playfair text-2xl text-amf-navy mt-2 leading-snug">
                      {p.title}
                    </h3>
                    <p className="font-lora text-amf-charcoal/80 mt-3 leading-relaxed">
                      {p.excerpt}
                    </p>
                    <p className="amf-overline text-amf-stone mt-3">{p.author}</p>
                  </div>
                  <div className="lg:col-span-3 lg:text-right">
                    {p.slug ? (
                      <Link
                        to={`/publications/${p.slug}`}
                        data-testid={`pub-read-${p.slug}`}
                        className="amf-overline inline-flex items-center gap-2 text-amf-burgundy hover:gap-3 transition-all"
                      >
                        Read Article <ArrowRight size={15} />
                      </Link>
                    ) : (
                      <button
                        data-testid="pub-download"
                        className="amf-overline inline-flex items-center gap-2 text-amf-burgundy hover:gap-3 transition-all"
                      >
                        <Download size={15} /> Download PDF
                      </button>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        ) : (
          <FadeIn className="border border-amf-gold/30 p-12 text-center">
            <FileText size={32} strokeWidth={1.3} className="text-amf-gold mx-auto" />
            <p className="font-playfair text-2xl text-amf-navy mt-5">Forthcoming</p>
            <p className="font-lora text-amf-stone mt-2">
              Subscribe to be notified when new publications are released.
            </p>
          </FadeIn>
        )}
      </Section>
    </div>
  );
}
